import { InjectionToken } from '@angular/core';
import { CategoryBasicView } from 'src/app/swagger-api';

export type CategoryRoutePath = 'diseases' | 'specialties' | 'charity-focus';

export const CATEGORY_ROUTES = new InjectionToken<CategoryRoute[]>('CATEGORY_ROUTES', {
  providedIn: 'root',
  factory: () => [],
});

export interface CategoryRoute {
  path: CategoryRoutePath;
  label: string;
}

export const DISEASES_ROUTE: CategoryRoute = {
  path: 'diseases',
  label: 'CATEGORY_DISEASES_LABEL',
};

export const SPECIALTIES_ROUTE: CategoryRoute = {
  path: 'specialties',
  label: 'CATEGORY_SPECIALTIES_LABEL',
};

export const CHARITY_FOCUS_ROUTE: CategoryRoute = {
  path: 'charity-focus',
  label: 'CATEGORY_CHARITY_FOCUS_LABEL',
};

export const categoryTypeLabel = (route: CategoryRoutePath) => {
  const typeLabels: Record<CategoryRoutePath, string> = {
    diseases: 'CATEGORY_DISEASE_LABEL',
    specialties: 'CATEGORY_SPECIALTY_LABEL',
    'charity-focus': 'CATEGORY_CHARITY_FOCUS_LABEL',
  };

  return typeLabels[route];
};

export const categoryPageTitle = (route: CategoryRoutePath) => {
  const titles: Record<CategoryRoutePath, string> = {
    diseases: 'CATEGORY_DISEASES_LABEL',
    specialties: 'CATEGORY_SPECIALTIES_LABEL',
    'charity-focus': 'CATEGORY_CHARITY_FOCUS_LABEL',
  };

  return titles[route];
};

export const categoryCreateTitle = (route: CategoryRoutePath) => {
  const titles: Record<CategoryRoutePath, string> = {
    diseases: 'CATEGORY_ADD_NEW_DISEASE_TITLE',
    specialties: 'CATEGORY_ADD_NEW_SPECIALTY_TITLE',
    'charity-focus': 'CATEGORY_ADD_NEW_CHARITY_FOCUS_TITLE',
  };

  return titles[route];
};

export const categoryUpdateDialogTitle = (route: CategoryRoutePath) => {
  const titles: Record<CategoryRoutePath, string> = {
    diseases: 'CATEGORY_EDIT_DISEASE_TITLE',
    specialties: 'CATEGORY_EDIT_SPECIALTY_TITLE',
    'charity-focus': 'CATEGORY_EDIT_CHARITY_FOCUS_TITLE',
  };

  return titles[route];
};

// Given an array of categories:
// i.e. [
//   -- Parent 1, with one child, Child 1
//   {entity_id: '1', title: 'Parent 1'},
//     {entity_id: '1-1', title: 'Child 1', parent_id: '1', parent_title: 'Parent 1'},
//   -- Parent 2 (no children)
//   {entity_id: '2', title: 'Parent 2'},
//   -- Parent 3 not present directly, as it wasn't selected
//      {entity_id: '3-1', title: 'Child 4', parent_id: '3', parent_title: 'Parent 3'},
// ]
// Format as:
// Parent 1: Child 1; Parent 2; Parent 3: Child 4
export const formatCategoryList = (categories: CategoryBasicView[]): string => {
  if (!categories || categories.length === 0) return '';

  const parentCategoriesSet = new Map<string, string>();
  const subcategoriesSet = new Map<string, string[]>();

  categories.forEach((category: CategoryBasicView) => {
    if (category.parent_id) {
      parentCategoriesSet.set(category.parent_id, category.parent_title);
      subcategoriesSet.set(category.parent_id, [...(subcategoriesSet.get(category.parent_id) || []), category.title]);
    } else {
      parentCategoriesSet.set(category.entity_id, category.title);
    }
  });

  // Format rules based on: https://dntlab.atlassian.net/browse/L2C-3863

  let text = '';

  // Sort parent categories alphabetically by title
  const sortedParentCategories = Array.from(parentCategoriesSet.entries()).sort((a, b) => a[1].localeCompare(b[1]));

  // We need to group subcategories by parent category
  sortedParentCategories.forEach(([parentCategoryId, parentCategoryTitle]) => {
    if (text) {
      text += '; '; // We separate categories with a semicolon
    }

    text += parentCategoryTitle;

    let subcategories = subcategoriesSet.get(parentCategoryId) || [];

    if (subcategories.length > 0) {
      subcategories = subcategories.sort((a, b) => a.localeCompare(b));

      text += ': ';
      text += subcategories.join(', '); // And subcategories with a comma
    }
  });

  return text;
};
